import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, ExternalLink, X, ChevronLeft, ChevronRight, Briefcase } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import axios from "axios";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "Alle" },
  { id: "software", label: "Software" },
  { id: "projekt", label: "Projekte" },
];

const typeBadge = {
  software: { label: "Software", bg: "bg-lab-primary", text: "text-white" },
  projekt: { label: "Projekt", bg: "bg-lab-accent", text: "text-lab-primary" },
};

const resolveImg = (src) => {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

function PortfolioModal({ project, onClose }) {
  const [activeImg, setActiveImg] = useState(0);
  const gallery = project.images?.length > 0 ? project.images : [project.image];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8" onClick={onClose}>
      <div className="absolute inset-0 bg-lab-primary/60 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} className="relative bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-lab-accent transition-colors" data-testid="modal-close">
          <X className="w-5 h-5 text-lab-primary" />
        </button>

        <div className="relative aspect-[16/9] overflow-hidden bg-lab-bg-light">
          <AnimatePresence mode="wait">
            <motion.img key={activeImg} src={resolveImg(gallery[activeImg])} alt={`${project.title} – Bild ${activeImg + 1}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full object-cover" />
          </AnimatePresence>
          {gallery.length > 1 && (
            <>
              <button onClick={() => setActiveImg(p => (p - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white" data-testid="gallery-prev">
                <ChevronLeft className="w-5 h-5 text-lab-primary" />
              </button>
              <button onClick={() => setActiveImg(p => (p + 1) % gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white" data-testid="gallery-next">
                <ChevronRight className="w-5 h-5 text-lab-primary" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm px-3 py-1 text-xs text-lab-primary font-medium">{activeImg + 1} / {gallery.length}</div>
            </>
          )}
        </div>

        {gallery.length > 1 && (
          <div className="flex gap-2 px-8 pt-4">
            {gallery.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-12 overflow-hidden border-2 transition-colors ${i === activeImg ? "border-lab-accent" : "border-transparent hover:border-lab-border"}`}>
                <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-8">
          <h2 className="font-playfair text-2xl font-semibold text-lab-primary mb-2">{project.title}</h2>
          <p className="text-lab-text-muted text-sm mb-6 leading-relaxed">{project.description}</p>
          {project.technologies?.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-lab-primary uppercase tracking-wider mb-3">Technologien</h4>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((t, i) => <span key={i} className="text-xs bg-lab-bg-light border border-lab-border text-lab-text-muted px-3 py-1">{t}</span>)}
              </div>
            </div>
          )}
          {project.url && (
            <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-lab-accent text-sm font-medium hover:underline">
              <ExternalLink className="w-4 h-4" /> Projekt ansehen
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/portfolio`).then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  usePageSeo({
    title: "Portfolio — Unsere Projekte | LAB336",
    description: "Entdecken Sie unser Portfolio: Software-Lösungen und Beratungsprojekte für Schweizer KMU. Digitalisierung, KI und Prozessoptimierung in der Praxis.",
    canonical: `${BASE_URL}/portfolio`,
  });

  const filtered = activeCategory === "all" ? projects : projects.filter(p => p.type === activeCategory);

  return (
    <div className="min-h-screen bg-white" data-testid="portfolio-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
              <Briefcase className="w-7 h-7 text-lab-accent" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Portfolio</h1>
              <p className="text-gray-400 mt-1">Software-Lösungen und Beratungsprojekte</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`text-sm px-5 py-2 border transition-colors ${activeCategory === cat.id ? "bg-lab-primary text-white border-lab-primary" : "bg-white text-lab-text-muted border-lab-border hover:border-lab-accent"}`}
              data-testid={`portfolio-filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <p className="text-lab-text-muted text-center py-16">Noch keine Projekte vorhanden.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((project, i) => {
              const badge = typeBadge[project.type] || typeBadge.projekt;
              return (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="bg-white border border-lab-border overflow-hidden group cursor-pointer hover:border-lab-accent transition-colors"
                  onClick={() => setSelected(project)}
                  data-testid={`portfolio-card-${project.id}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-lab-bg-light">
                    <img src={resolveImg(project.image)} alt={project.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-4 left-4">
                      <span className={`${badge.bg} ${badge.text} px-3 py-1 text-xs font-semibold tracking-wide`}>{badge.label}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-playfair text-lg font-semibold text-lab-primary group-hover:text-lab-accent transition-colors mb-2">{project.title}</h3>
                    <p className="text-lab-text-muted text-sm leading-relaxed mb-4 line-clamp-2">{project.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies?.slice(0, 3).map((tech, j) => (
                        <span key={j} className="text-xs bg-lab-bg-light text-lab-text-muted px-2 py-1">{tech}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-lab-border">
                      <div className="flex items-center gap-2 text-lab-text-muted text-sm">
                        <Calendar className="w-4 h-4" /><span>{project.year}</span>
                      </div>
                      <span className="text-lab-accent font-medium text-sm flex items-center gap-1">Details <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-lab-text-muted mb-4">Haben Sie ein ähnliches Projekt im Sinn?</p>
          <Link to="/kontakt" className="inline-block bg-lab-primary text-white px-8 py-3 text-sm font-semibold hover:bg-lab-primary/90 transition-colors" data-testid="portfolio-cta">
            Projekt besprechen
          </Link>
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <PortfolioModal project={selected} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
